import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Tv, BarChart2, Clock, Trophy, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'Max',          color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#aaaaaa' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const delta = (Date.now() - new Date(iso)) / 1000;
  if (delta < 86400) return 'Today';
  if (delta < 172800) return 'Yesterday';
  const days = Math.floor(delta / 86400);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function History() {
  const { user, getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(res.data);
      } catch (err) {
        console.error('Stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchStats();
  }, [user, getToken]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <BarChart2 className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
        <p className="text-neutral-500">Could not load your stats.</p>
      </div>
    );
  }

  const topService = stats.services?.[0];

  return (
    <div className="max-w-2xl mx-auto pb-12 space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>Watch History</h1>
        <p className="text-neutral-500 mt-1">Your watching stats and recently finished shows</p>
      </div>

      {/* Big stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <Tv className="w-5 h-5 text-violet-400 mx-auto mb-2" />
          <p className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit' }}>{stats.total}</p>
          <p className="text-xs text-neutral-500 mt-1">Total Shows</p>
        </div>
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <Trophy className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
          <p className="text-3xl font-black text-emerald-400" style={{ fontFamily: 'Outfit' }}>{stats.watched}</p>
          <p className="text-xs text-neutral-500 mt-1">Finished</p>
        </div>
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <TrendingUp className="w-5 h-5 text-[#4A9EFF] mx-auto mb-2" />
          <p className="text-3xl font-black text-[#4A9EFF]" style={{ fontFamily: 'Outfit' }}>{stats.watching}</p>
          <p className="text-xs text-neutral-500 mt-1">Watching</p>
        </div>
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-5 text-center">
          <Clock className="w-5 h-5 text-orange-400 mx-auto mb-2" />
          <p className="text-3xl font-black text-orange-400" style={{ fontFamily: 'Outfit' }}>
            {stats.estimated_hours >= 1000 ? `${(stats.estimated_hours / 1000).toFixed(1)}k` : stats.estimated_hours}
          </p>
          <p className="text-xs text-neutral-500 mt-1">Est. Hours</p>
        </div>
      </div>

      {/* Completion rate */}
      {stats.total > 0 && (
        <div className="p-5 rounded-2xl bg-[#0A0A0A] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-400" />
              <p className="text-sm font-semibold">Completion Rate</p>
            </div>
            <span className={`text-sm font-black ${
              stats.completion_rate >= 80 ? 'text-emerald-400' :
              stats.completion_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>{stats.completion_rate}%</span>
          </div>
          <div className="w-full h-3 rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                stats.completion_rate >= 80 ? 'bg-emerald-400' :
                stats.completion_rate >= 50 ? 'bg-yellow-400' : 'bg-red-400'
              }`}
              style={{ width: `${stats.completion_rate}%` }}
            />
          </div>
          <p className="text-xs text-neutral-600 mt-2">
            {stats.watched} finished out of {stats.total} total
            {stats.want_to_watch > 0 ? ` · ${stats.want_to_watch} in queue` : ''}
          </p>
        </div>
      )}

      {/* Service breakdown */}
      {stats.services?.length > 0 && (
        <div>
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">By Service</p>
          <div className="space-y-2">
            {stats.services.map(s => {
              const meta = SERVICE_META[s.service_id];
              const pct = stats.total > 0 ? Math.round((s.count / stats.total) * 100) : 0;
              return (
                <div key={s.service_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta?.color || '#666' }} />
                  <p className="text-sm font-medium flex-1">{meta?.name || s.service_id}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta?.color || '#666' }} />
                    </div>
                    <span className="text-xs text-neutral-500 w-6 text-right">{s.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {topService && SERVICE_META[topService.service_id] && (
            <p className="text-xs text-neutral-600 mt-2 text-center">
              Your most-used service is <span style={{ color: SERVICE_META[topService.service_id]?.color }}>{SERVICE_META[topService.service_id]?.name}</span>
            </p>
          )}
        </div>
      )}

      {stats.top_genres?.length > 0 && (
        <div>
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Top Genres</p>
          <div className="flex flex-wrap gap-2">
            {stats.top_genres.map(g => (
              <div key={g.name} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A0A0A] border border-white/5">
                <span className="text-sm font-semibold text-white">{g.name}</span>
                <span className="text-xs text-neutral-500">{g.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recently finished */}
      {stats.recent_finishes?.length > 0 && (
        <div>
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Recently Finished</p>
          <div className="space-y-2">
            {stats.recent_finishes.map((show, i) => {
              const meta = SERVICE_META[show.service_id];
              return (
                <Link
                  key={i}
                  to={show.tmdb_id ? `/show/${show.tmdb_id}` : '#'}
                  className="flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/30 transition-all no-underline group"
                >
                  <div className="w-10 h-14 rounded-lg bg-white/5 overflow-hidden flex-shrink-0">
                    {show.image_url
                      ? <img src={show.image_url} alt={show.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                      : <div className="w-full h-full flex items-center justify-center"><Tv className="w-4 h-4 text-neutral-600" /></div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{show.title}</p>
                    <p className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
                      {meta && <span style={{ color: meta.color }}>{meta.name}</span>}
                      {meta && <span className="text-neutral-700">·</span>}
                      <span>{timeAgo(show.finished_at)}</span>
                    </p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 flex-shrink-0">
                    ✓ Done
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {stats.total === 0 && (
        <div className="text-center py-16">
          <Tv className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <p className="text-neutral-500 text-sm">No shows yet.</p>
          <Link to="/watchlist" className="text-violet-400 text-sm hover:text-violet-300 mt-2 inline-block">
            Add shows to your watchlist →
          </Link>
        </div>
      )}
    </div>
  );
}
